import React from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { ErrorBoundary } from "react-error-boundary"

import Logo from "@components/prototypes/Logo/Logo"
import Templates from "@components/ui/Templates"
import { useController, useStore } from "@store"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import WorkbookReportFeedbackWidget from "@components/prototypes/StarRatings/WorkbookReportFeedbackWidget"
import Text from "@components/ui/Typography/Text"

import Report from "./Report"
import { DownloadReportButton } from "./DownloadReportButton"

import styles from "./WorkbookReport.module.sass"

const WorkbookReportPage: React.FC = observer(() => {
  const { reportId } = useParams()

  if (!reportId)
    throw new Error("WorkbookReportPage requires reportId path parameter")

  const { workbookReportsStore } = useStore()

  const { unifiedMatrixController } = useController()

  const report = workbookReportsStore.getById(reportId)

  React.useEffect(() => {
    unifiedMatrixController.pullReport(reportId, true)
  }, [reportId])

  React.useEffect(() => {
    if (report.status !== "in-progress")
      workbookReportsStore.removeTaskNotification(reportId)
  }, [report.status, reportId])

  return (
    <div className={styles.root}>
      <ErrorBoundary
        fallback={
          <NotFound>
            Failed to display report.
            <br />
            Unexpected format
          </NotFound>
        }
        key={reportId}
      >
        <div className={styles.box}>
          <Templates.Header
            left={<Logo height={40} withText />}
            right={
              report.status === "completed" &&
              report.data != null && (
                <DownloadReportButton report={report.data} />
              )
            }
            className={styles.header}
          />
          <div className={styles.body}>
            {report.data != null ? (
              <Report data={report.data} />
            ) : report.isLoading ? (
              <Loader size="large" fluid />
            ) : (
              <NotFound fluid />
            )}
          </div>

          {report.status === "completed" && (
            <Templates.Header
              className={styles.footer}
              right={
                <div className={styles.ratingContainer}>
                  <Text variant="h5" color="text70Color">
                    How helpful was this report?
                  </Text>
                  <WorkbookReportFeedbackWidget reportId={reportId} />
                </div>
              }
            />
          )}
        </div>
      </ErrorBoundary>
    </div>
  )
})

export default WorkbookReportPage
